import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, UserPlus, LogIn, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ApplyWelcome = () => {
  const { language } = useLanguage();

  const benefits = [
    {
      es: 'Postula a múltiples desafíos de innovación',
      en: 'Apply to multiple innovation challenges'
    },
    {
      es: 'Guarda y edita tus postulaciones',
      en: 'Save and edit your applications'
    },
    {
      es: 'Recibe notificaciones sobre el estado de tus postulaciones',
      en: 'Receive notifications about your application status'
    },
    {
      es: 'Accede a tu panel personalizado',
      en: 'Access your personalized dashboard'
    },
    {
      es: 'Conecta con organizaciones y evaluadores',
      en: 'Connect with organizations and evaluators'
    }
  ];

  return (
    <div className="min-h-screen bg-sand-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-purple-700 py-16 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {language === 'es' 
              ? '¡Únete a ColaboraChile para Postular!' 
              : 'Join ColaboraChile to Apply!'}
          </h1>
          <p className="text-xl text-white/90 mb-8">
            {language === 'es'
              ? 'Crea tu cuenta gratuita y comienza a postular a desafíos de innovación en minutos'
              : 'Create your free account and start applying to innovation challenges in minutes'}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Benefits Card */}
          <Card className="border-2 border-gray-200">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-black mb-6">
                {language === 'es' ? '¿Por qué registrarse?' : 'Why register?'}
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">
                      {language === 'es' ? benefit.es : benefit.en}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Action Cards */}
          <div className="space-y-6">
            {/* New User Card */}
            <Card className="border-2 border-primary hover:shadow-lg transition-all">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-primary/10 rounded-full">
                    <UserPlus className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-black">
                      {language === 'es' ? 'Nuevo Usuario' : 'New User'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {language === 'es' ? 'Crea tu cuenta gratis' : 'Create your free account'}
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 mb-6">
                  {language === 'es'
                    ? 'Regístrate en segundos y comienza a postular a los mejores desafíos de innovación en Chile.'
                    : 'Sign up in seconds and start applying to the best innovation challenges in Chile.'}
                </p>
                <Button
                  asChild
                  size="lg"
                  className="w-full bg-secondary text-white hover:bg-secondary/90"
                >
                  <Link to="/register">
                    {language === 'es' ? 'Crear Cuenta' : 'Create Account'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Existing User Card */}
            <Card className="border-2 border-gray-200 hover:shadow-lg transition-all">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-gray-100 rounded-full">
                    <LogIn className="w-8 h-8 text-gray-700" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-black">
                      {language === 'es' ? 'Ya tengo cuenta' : 'I have an account'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {language === 'es' ? 'Inicia sesión' : 'Sign in'}
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 mb-6">
                  {language === 'es'
                    ? 'Si ya tienes una cuenta, inicia sesión para continuar con tu postulación.'
                    : 'If you already have an account, sign in to continue with your application.'}
                </p>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="w-full border-2 border-gray-300 hover:border-primary"
                >
                  <Link to="/login">
                    {language === 'es' ? 'Iniciar Sesión' : 'Sign In'}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <Card className="border-2 border-gray-200 bg-gradient-to-r from-sand-50 to-white">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-black mb-4">
                {language === 'es' ? '¿Cómo funciona?' : 'How does it work?'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                      1
                    </div>
                    <h4 className="font-semibold text-black">
                      {language === 'es' ? 'Regístrate' : 'Register'}
                    </h4>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {language === 'es'
                      ? 'Crea tu cuenta con tu email y elige tu tipo de perfil'
                      : 'Create your account with your email and choose your profile type'}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                      2
                    </div>
                    <h4 className="font-semibold text-black">
                      {language === 'es' ? 'Explora' : 'Explore'}
                    </h4>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {language === 'es'
                      ? 'Navega por los desafíos disponibles y encuentra el ideal para ti'
                      : 'Browse available challenges and find the perfect one for you'}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                      3
                    </div>
                    <h4 className="font-semibold text-black">
                      {language === 'es' ? 'Postula' : 'Apply'}
                    </h4>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {language === 'es'
                      ? 'Completa tu postulación y envíala a los desafíos que te interesan'
                      : 'Complete your application and submit it to challenges that interest you'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ApplyWelcome;
